import React, {useState} from 'react';
import moment from 'moment';
import pick from 'lodash/pick';
import omit from 'lodash/omit';
import uniq from 'lodash/uniqBy';

import Box from '@mui/material/Box';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import AddressField from '../common/AddressField';
import CustomInput from '../common/CustomInput';
import CustomInputLabel from '../common/CustomInputLabel';
import CustomSelect from '../common/CustomSelect';
import CustomTextField from '../common/CustomTextField';
import ChoiceField from '../common/ChoiceField';
import DayPicker from '../common/DayPicker';
import DateField from '../common/DateField';
import TimeField from '../common/TimeField';

import {useGetSchedulesQuery} from '../../redux/services/scheduleApi';
import {
  useGetProviderAvailabilitiesQuery
} from '../../redux/services/providerAvailabilityApi';
import {getDays} from '../../helpers/scheduling';
import config from '../../config/config';
import TimeSlot from './TimeSlot';

const getDayQueryParams = ({providerId, serviceCategory, date}) => {
  let query = {
    service_category: serviceCategory,
    start_time: moment(date).startOf('day').toISOString(),
    end_time: moment(date).endOf('day').toISOString(),
  };
  if (providerId) query.provider_id = providerId;
  return query;
};

const getWeekQueryParams = ({providerId, serviceCategory, date}) => {
  let query = {
    service_category: serviceCategory,
    start_time: moment(date).startOf('week').toISOString(),
    end_time: moment(date).endOf('week').toISOString(),
  };
  if (providerId) query.provider_id = providerId;
  return query;
};

const availabilityMatchesTime = ({time}) => (availability) => {
  if (!time) return false;
  const momentTime = moment(time);
  const startMinute = momentTime.hour() * 60 + momentTime.minute();
  const match = (availability.day_availabilities || []).find(da => 
    da.day === momentTime.format('ddd').toUpperCase()
    && da.start_minute <= startMinute
    && da.end_minute >= startMinute
  );
  return !!match;
};
  
const LocationChoiceField = props => {
  const childProps = {
    onChange: props.onChange,
    color: 'purple',
    defaultValue: null,
    label: 'Location Type',
    required: true,
    options: [
      {label: '🏋️ Facility', value: 'PARTNER'},
      {label: '🏠 In-Home', value: 'HOME'},
      {label: '🚗 Mobile', value: 'MOBILE'},
      {label: '💻 Virtual', value: 'VIRTUAL'},
    ].filter(opt => (props?.locationTypes || []).includes(opt.value))
  };
  return (
    <Box style={{minWidth: '400px'}}>
      <ChoiceField {...childProps} />
    </Box>
  );
};

const LocationDetailsForm = props => {
  const {locationType, providerAvailability, sessionConfig} = props;
  if (locationType === 'VIRTUAL') {
    return (
      <CustomTextField
        label='Meeting Link'
        required={true}
        color='purple'
        options={providerAvailability?.session_meeting_url}
        minWidth='350px'
        defaultValue={props}
        onChange={(e, val) => props.onChange({session_meeting_url: e?.target?.value})}
      />
    );
  }
  if (locationType === 'PARTNER') {
    return (
      <CustomSelect
        fullWidth
        label='Location'
        required={true}
        color='purple'
        options={(sessionConfig?.locations || []).map(location => ({
          label: location?.name,
          value: location.id,
        }))}
        minWidth='350px'
        onChange={(e, val) => props.onChange({location_id: val})}
      />
    );
  }
  if (locationType === 'HOME') {
    // return <CustomInput label='Location'>🏠 Patient's Home</CustomInput>;
    return null;
  }
  if (locationType === 'MOBILE' && !props.isPatient) {
    return (
      <Box style={{minWidth: '400px', width: '100%'}}>
        <AddressField
          label='Address'
          required={true}
          color='purple'
          minWidth='400px'
          fullWidth
          editable={false}
          onChange={(e, val) => props.onChange({address: val})}
        />
      </Box>
    );
  }
};

const parseLocationDetails = (details, locationType) => {
  if (locationType === 'VIRTUAL') {
    return {session_meeting_url: details};
  }
  if (locationType === 'PARTNER') {
    return {location_id: details};
  }
  if (locationType === 'HOME') {
    return {};
  }
  if (locationType === 'MOBILE') {
    return {address: details};
  }
  return {};
};

const handleFieldChange = ({
  formData, 
  setFormData, 
  onChange
}) => fieldName => (e, val) => {
  let newData;
  if (fieldName === 'start_time') {
    // when start time changes, reset location details
    newData = pick(formData, 'override_schedule')
  } else if (fieldName === 'location_type') {
    newData = omit(formData, 'address', 'location_id', 'session_meeting_url');
  } else {
    newData = omit(formData, 'override_schedule');
  }
  if (fieldName === 'start_time' && val) {
    newData.start_time = val.start_time;
    newData.provider_availability_id = val.provider_availability_id;
  } else if (fieldName === 'override_schedule' && val) {
    newData.override_schedule = true;
  } else if (fieldName === 'location_details') {
    newData = {...newData, ...parseLocationDetails(val, formData.location_type)};
  } else {
    newData[fieldName] = val;
  }
  setFormData(newData);
  onChange(e, newData);
};

const SchedulerInput = ({
  providerId,
  serviceCategory = 'PHYSICAL_THERAPY',
  color = 'primary',
  allowOverrideSchedule = true,
  onChange,
  isPatient = false,
}) => {
  const [formData, setFormData] = useState({});
  const [date, setDate] = useState(new Date());
  const override = !!formData?.override_schedule || false;
  const time = formData.start_time || null;
  const timeSlotsQuery = useGetSchedulesQuery(getDayQueryParams({
    providerId, 
    serviceCategory, 
    date
  }));
  const daysQuery = useGetSchedulesQuery(getWeekQueryParams({
    providerId,
    serviceCategory,
    date,
  }));
  const availabilitiesQuery = useGetProviderAvailabilitiesQuery({
    provider_id: providerId
  });
  const availabilities = availabilitiesQuery?.data?.data || [];
  // map to the dates that are available
  const availableDates = (daysQuery?.data?.data || []).map(
    slot => moment(slot.start_time).format('YYYY-MM-DD')
  );
  const availableDays = availableDates.map(
    dateStr => moment(dateStr).format('ddd').toUpperCase()
  );
  const disabledDays = getDays().filter(d => !availableDays.includes(d));
  const timeSlots = uniq(timeSlotsQuery?.data?.data || [], s => s.start_time);
  const availability = availabilities.find(availabilityMatchesTime({time}));
  const sessionConfig = (
    !availability || override
    ? null
    : availability.configs.find(conf => 
      conf.session_type.service_category === serviceCategory
    )
  );
  const locationTypes = (
    override
    ? ['PARTNER', 'HOME', 'MOBILE', 'VIRTUAL']
    : (sessionConfig?.location_types || [])
  );
  const facilities = override ? [] : (sessionConfig?.locations || []);
  const onFieldChange = handleFieldChange({formData, setFormData, onChange});
  return (
    <Box>
      {!!allowOverrideSchedule && <Box style={{paddingTop: '1em', paddingBottom: '1em'}}>
        <ChoiceField 
          defaultValue={false}
          color={color}
          options={[
            {value: false, label: 'Show Schedule'},
            {value: true, label: 'Override Schedule'},
          ]}
          onChange={(e, val) => onFieldChange('override_schedule')(e, val)}
        />
      </Box>}
      {!override && 
        <Stack>
          <Stack 
            direction='row' 
          >
            <Stack spacing={1} flex={1}>
              <CustomInputLabel required={true}>Day</CustomInputLabel>
              <Stack alignItems='center'>
                <DayPicker
                  color={color}
                  date={date}
                  onChange={(e, val) => {
                    setDate(val);
                    onFieldChange('start_time')(e, null);
                  }}
                  disabledDays={disabledDays}
                />
              </Stack>
              <Stack 
                direction='row' 
                spacing={2} 
                justifyContent='center'
                alignItems='center'
              >
                <IconButton 
                  size='small' 
                  color={color}
                  onClick={e => {
                    setDate(moment(date).subtract(1, 'week').toDate());
                    onFieldChange('start_time')(e, null);
                  }}
                >
                  <ChevronLeft /> 
                </IconButton>
                <Typography variant='body2'>
                  {moment(date).startOf('week').format('MMM D')} 
                  {' '}-{' '}
                  {moment(date).endOf('week').format('MMM D')} 
                </Typography>
                <IconButton 
                  size='small' 
                  color={color}
                  onClick={e => {
                    setDate(moment(date).add(1, 'week').toDate());
                    onFieldChange('start_time')(e, null);
                  }}
                >
                  <ChevronRight /> 
                </IconButton>
              </Stack>
            </Stack>
            <Stack spacing={1} pl={2} flex={1}>
              <CustomInputLabel required={true}>Time</CustomInputLabel>
              <Stack direction='row' gap={1} pt={1} flexWrap='wrap'>
                  {(timeSlots || []).map((slot, i) => 
                    <TimeSlot
                      key={`slot-${i}`}
                      timeSlot={slot}
                      selected={formData.start_time === slot.start_time}
                      onClick={(e, slot) => {
                        onFieldChange('start_time')(e, slot);
                      }}
                      color={color}
                    />
                  )
                  }
              </Stack>
            </Stack>
          </Stack>
        </Stack> 
      }
      {override && 
        <Stack direction='row' spacing={2} alignItems='center'>
          <Box style={{paddingTop: '1em'}}>
            <DateField
              label='Date'
              defaultValue={date}
              color={color}
              onChange={(e, val) => setDate(val)}
            />
          </Box>
          <TimeField
            label='Time'
            defaultValue={formData.start_time}
            onChange={(e, val) => {
              const datetime = moment(date).set({
                minute: moment(val).minute(),
                hour: moment(val).hour(),
              }).toISOString();
              onFieldChange('start_time')(e, {start_time: datetime});
            }}
            color={color}
          />
        </Stack>
      }
      {(override || !!availability) && locationTypes.length > 0 &&
        <Stack direction='row' spacing={2} pt={2}>
          <LocationChoiceField
            key={formData.start_time}
            locationTypes={locationTypes}
            onChange={(e, val) => {
              onFieldChange('location_type')(e, val);
            }}
          />
          {!!formData.location_type &&
            <LocationDetailsForm
              key={formData.start_time}
              locationType={formData.location_type} 
              providerAvailability={availability}
              sessionConfig={sessionConfig}
              isPatient={isPatient}
              onChange={(e, val) => {
                onFieldChange('location_details')(e, val);
              }}
            />
          }
        </Stack>
      }
    </Box>
  );
};

export default SchedulerInput;